if (!customElements.get('delivery-date-picker')) {
  customElements.define(
    'delivery-date-picker',
    class DeliveryDatePicker extends HTMLElement {
      connectedCallback() {
        if (this.trigger) return;

        this.trigger = this.querySelector('[data-picker-trigger]');
        this.valueLabel = this.querySelector('[data-picker-value]');
        this.input = this.querySelector('[data-delivery-date-input]');
        this.panel = this.querySelector('[data-picker-panel]');
        this.monthLabel = this.querySelector('[data-picker-month]');
        this.prevButton = this.querySelector('[data-picker-prev]');
        this.nextButton = this.querySelector('[data-picker-next]');
        this.weekdayRow = this.querySelector('[data-picker-weekdays]');
        this.dayRows = this.querySelector('[data-picker-days]');
        this.error = this.querySelector('[data-delivery-date-error]');
        this.selectedDate = null;

        this.timeZone = this.dataset.timeZone || undefined;
        this.cutoffHour = Number(this.dataset.cutoffHour) || 12;
        this.monthsAhead = Number(this.dataset.monthsAhead) || 6;
        this.closedDays = new Set(
          (this.dataset.closedDays || '').split(',').filter(Boolean).map(Number)
        );
        this.parseClosedDates(this.dataset.closedDates || '');

        // Names and patterns come from the theme locale files, so every browser shows the
        // same language regardless of its built-in date locale data. Weeks start on Monday.
        this.months = (this.dataset.months || '').split(',');
        this.weekdaysShort = (this.dataset.weekdaysShort || '').split(',');
        this.weekdaysLong = (this.dataset.weekdaysLong || '').split(',');

        this.renderWeekdays();
        this.updateRange();

        this.trigger.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
        this.prevButton.addEventListener('click', () => this.shiftMonth(-1));
        this.nextButton.addEventListener('click', () => this.shiftMonth(1));
        this.dayRows.addEventListener('click', this.onDayClick.bind(this));
        this.dayRows.addEventListener('keydown', this.onDayKeydown.bind(this));
        this.panel.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          event.stopPropagation();
          this.close();
        });
        this.addEventListener('focusout', (event) => {
          if (this.isOpen && event.relatedTarget && !this.contains(event.relatedTarget)) {
            this.close({ restoreFocus: false });
          }
        });
        this.onOutsidePointer = (event) => {
          if (!this.contains(event.target)) this.close({ restoreFocus: false });
        };
      }

      get isOpen() {
        return !this.panel.hidden;
      }

      focus() {
        this.trigger.focus();
      }

      // "Today" and the cutoff hour follow the shop's time zone, not the visitor's.
      shopToday() {
        const parts = Object.fromEntries(
          new Intl.DateTimeFormat('en-CA', {
            timeZone: this.timeZone,
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            hourCycle: 'h23',
          })
            .formatToParts(new Date())
            .map(({ type, value }) => [type, Number(value)])
        );
        return { date: new Date(parts.year, parts.month - 1, parts.day), hour: parts.hour };
      }

      // Merchant-entered closed dates, one per line (commas also work): "25.12.2026",
      // a range "30.12.2026 - 02.01.2027", or "01.01." without a year to repeat every year.
      parseClosedDates(text) {
        this.closedIsoDates = new Set();
        this.closedMonthDays = new Set();
        const datePattern = /^(\d{1,2})\.(\d{1,2})(?:\.(\d{4}))?\.?$/;

        text
          .split(/[\n,;]+/)
          .map((entry) => entry.trim())
          .filter(Boolean)
          .forEach((entry) => {
            const [from, to = from] = entry.split(/\s*[-–]\s*/);
            const start = from.replace(/\s+/g, '').match(datePattern);
            const end = to.replace(/\s+/g, '').match(datePattern);
            if (!start || !end) return;

            const yearly = !start[3];
            // 2000 is a leap year, so a yearly 29.02. is kept.
            const startDate = new Date(yearly ? 2000 : Number(start[3]), start[2] - 1, start[1]);
            const endDate = new Date(yearly ? 2000 : Number(end[3] || start[3]), end[2] - 1, end[1]);
            if (yearly && endDate < startDate) endDate.setFullYear(2001); // e.g. 31.12. - 02.01.

            for (let day = new Date(startDate), count = 0; day <= endDate && count < 366; count++) {
              if (yearly) this.closedMonthDays.add(this.toMonthDay(day));
              else this.closedIsoDates.add(this.toIso(day));
              day.setDate(day.getDate() + 1);
            }
          });
      }

      toMonthDay(date) {
        return `${date.getMonth() + 1}-${date.getDate()}`;
      }

      isClosedDate(date) {
        return (
          this.closedDays.has(date.getDay()) ||
          this.closedIsoDates.has(this.toIso(date)) ||
          this.closedMonthDays.has(this.toMonthDay(date))
        );
      }

      updateRange() {
        const { date: minDate, hour } = this.shopToday();
        if (hour >= this.cutoffHour) minDate.setDate(minDate.getDate() + 1);
        for (let i = 0; i < 366 && this.isClosedDate(minDate); i++) minDate.setDate(minDate.getDate() + 1);

        this.minDate = minDate;
        this.maxDate = new Date(minDate.getFullYear(), minDate.getMonth() + this.monthsAhead + 1, 0);
      }

      isAvailable(date) {
        return date >= this.minDate && date <= this.maxDate && !this.isClosedDate(date);
      }

      toIso(date) {
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${date.getFullYear()}-${month}-${day}`;
      }

      fromIso(value) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
      }

      // The order property is written the way it is read locally, e.g. "24.10.2026.".
      toPropertyValue(date) {
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${day}.${month}.${date.getFullYear()}.`;
      }

      formatDate(pattern, date) {
        const values = {
          weekday: this.weekdaysLong[(date.getDay() + 6) % 7],
          day: date.getDate(),
          month: this.months[date.getMonth()],
          year: date.getFullYear(),
        };
        return pattern.replace(/\[(weekday|day|month|year)\]/g, (_, key) => values[key]);
      }

      formatMonth(date) {
        return this.formatDate(this.dataset.monthFormat, date);
      }

      formatFull(date) {
        return this.formatDate(this.dataset.fullFormat, date);
      }

      renderWeekdays() {
        this.weekdayRow.innerHTML = '';
        for (let i = 0; i < 7; i++) {
          const cell = document.createElement('th');
          cell.scope = 'col';
          cell.abbr = this.weekdaysLong[i];
          cell.textContent = this.weekdaysShort[i];
          this.weekdayRow.appendChild(cell);
        }
      }

      open() {
        this.updateRange();
        const selected = this.selectedDate;
        this.focusedDate = selected && this.isAvailable(selected) ? selected : new Date(this.minDate);
        this.renderMonth();
        this.panel.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        document.addEventListener('pointerdown', this.onOutsidePointer);
        this.focusDay();
      }

      close({ restoreFocus = true } = {}) {
        if (!this.isOpen) return;
        this.panel.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        document.removeEventListener('pointerdown', this.onOutsidePointer);
        if (restoreFocus) this.trigger.focus();
      }

      shiftMonth(offset) {
        const target = new Date(this.focusedDate.getFullYear(), this.focusedDate.getMonth() + offset, 1);
        const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
        target.setDate(Math.min(this.focusedDate.getDate(), lastDay));
        this.moveFocus(target, { focus: false });
      }

      moveFocus(date, { focus = true } = {}) {
        const clamped = new Date(Math.min(Math.max(date, this.minDate), this.maxDate));
        const monthChanged =
          clamped.getMonth() !== this.focusedDate.getMonth() ||
          clamped.getFullYear() !== this.focusedDate.getFullYear();
        this.focusedDate = clamped;
        if (monthChanged) this.renderMonth();
        else this.updateRovingFocus();
        if (focus) this.focusDay();
      }

      renderMonth() {
        const year = this.focusedDate.getFullYear();
        const month = this.focusedDate.getMonth();
        const selectedIso = this.selectedDate ? this.toIso(this.selectedDate) : '';
        const todayIso = this.toIso(this.shopToday().date);
        const leadingBlanks = (new Date(year, month, 1).getDay() + 6) % 7;
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        this.monthLabel.textContent = this.formatMonth(this.focusedDate);
        this.prevButton.disabled =
          year < this.minDate.getFullYear() ||
          (year === this.minDate.getFullYear() && month <= this.minDate.getMonth());
        this.nextButton.disabled =
          year > this.maxDate.getFullYear() ||
          (year === this.maxDate.getFullYear() && month >= this.maxDate.getMonth());

        this.dayRows.innerHTML = '';
        let row = document.createElement('tr');
        for (let i = 0; i < leadingBlanks; i++) row.appendChild(document.createElement('td'));

        for (let day = 1; day <= daysInMonth; day++) {
          const date = new Date(year, month, day);
          const iso = this.toIso(date);
          const label = this.formatFull(date);
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'delivery-picker__day';
          button.dataset.date = iso;
          button.textContent = day;
          if (this.isAvailable(date)) {
            button.setAttribute('aria-label', label);
          } else {
            button.setAttribute('aria-label', `${label}, ${this.dataset.unavailableLabel}`);
            button.setAttribute('aria-disabled', 'true');
          }
          if (iso === selectedIso) button.setAttribute('aria-pressed', 'true');
          if (iso === todayIso) button.setAttribute('aria-current', 'date');

          const cell = document.createElement('td');
          cell.appendChild(button);
          row.appendChild(cell);
          if (row.children.length === 7) {
            this.dayRows.appendChild(row);
            row = document.createElement('tr');
          }
        }
        if (row.children.length) {
          while (row.children.length < 7) row.appendChild(document.createElement('td'));
          this.dayRows.appendChild(row);
        }

        this.updateRovingFocus();
      }

      updateRovingFocus() {
        const focusedIso = this.toIso(this.focusedDate);
        this.dayRows.querySelectorAll('.delivery-picker__day').forEach((button) => {
          button.tabIndex = button.dataset.date === focusedIso ? 0 : -1;
        });
      }

      focusDay() {
        this.dayRows.querySelector(`[data-date="${this.toIso(this.focusedDate)}"]`)?.focus();
      }

      onDayClick(event) {
        const button = event.target.closest('.delivery-picker__day');
        if (!button || button.getAttribute('aria-disabled') === 'true') return;
        this.select(this.fromIso(button.dataset.date));
      }

      onDayKeydown(event) {
        const date = new Date(this.focusedDate);
        const weekday = (date.getDay() + 6) % 7;
        switch (event.key) {
          case 'ArrowLeft':
            date.setDate(date.getDate() - 1);
            break;
          case 'ArrowRight':
            date.setDate(date.getDate() + 1);
            break;
          case 'ArrowUp':
            date.setDate(date.getDate() - 7);
            break;
          case 'ArrowDown':
            date.setDate(date.getDate() + 7);
            break;
          case 'Home':
            date.setDate(date.getDate() - weekday);
            break;
          case 'End':
            date.setDate(date.getDate() + (6 - weekday));
            break;
          case 'PageUp':
          case 'PageDown':
            event.preventDefault();
            this.shiftMonth(event.key === 'PageUp' ? -1 : 1);
            this.focusDay();
            return;
          default:
            return;
        }
        event.preventDefault();
        this.moveFocus(date);
      }

      select(date) {
        this.selectedDate = date;
        this.input.value = this.toPropertyValue(date);
        this.valueLabel.textContent = this.formatFull(date);
        this.valueLabel.classList.remove('is-placeholder');
        this.showError(false);
        this.close();
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      clear() {
        this.selectedDate = null;
        this.input.value = '';
        this.valueLabel.textContent = this.dataset.placeholder;
        this.valueLabel.classList.add('is-placeholder');
      }

      validate() {
        this.updateRange();
        let message = false;

        if (!this.selectedDate) {
          message = this.dataset.requiredError;
        } else if (!this.isAvailable(this.selectedDate)) {
          // The page stayed open past the cutoff, so the chosen day is no longer available.
          this.clear();
          message = this.dataset.tooEarlyError;
        }

        this.showError(message);
        return !message;
      }

      showError(message) {
        this.error.toggleAttribute('hidden', !message);
        this.error.textContent = message || '';
        this.trigger.setAttribute('aria-invalid', message ? 'true' : 'false');
      }
    }
  );
}
